import React, { useMemo } from 'react';
import { diffLines } from 'diff';
import { parseDiff } from 'react-diff-view';
import { Diff as ReactDiffViewer, Hunk } from 'react-diff-view';
import 'react-diff-view/style/index.css';
import { ScrollableCodeContainer } from '../claim-details/claim-timeline/claim-timeline.styles';
import JsonView from '@uiw/react-json-view';
import * as S from './payload-comparison-viewer.styles';

export interface PayloadComparisonViewerProps {
    currentX12: string;
    previousX12?: string;
    previousTransmissionId?: string;
    showDiff: boolean;
    previousLabel?: string;
    currentLabel?: string;
    stripNullFields?: boolean;
}

export const PayloadComparisonViewer: React.FC<PayloadComparisonViewerProps> = ({
    currentX12,
    previousX12,
    previousTransmissionId,
    showDiff,
    previousLabel = 'Original Transmission',
    currentLabel = 'Current Transmission',
    stripNullFields = false,
}) => {
    const normalizeJsonValue = (value: unknown): unknown => {
        if (Array.isArray(value)) {
            return value.map(item => normalizeJsonValue(item));
        }
        if (value && typeof value === 'object') {
            const record = value as Record<string, unknown>;
            return Object.keys(record)
                .sort((a, b) => a.localeCompare(b))
                .reduce<Record<string, unknown>>((acc, key) => {
                    const normalizedValue = normalizeJsonValue(record[key]);
                    if (stripNullFields && normalizedValue === null) {
                        return acc;
                    }
                    acc[key] = normalizedValue;
                    return acc;
                }, {});
        }
        return value;
    };

    const safeParseJson = (text: string): unknown | null => {
        try {
            return JSON.parse(text);
        } catch {
            return null;
        }
    };

    const toComparableText = (text: string): string => {
        const parsed = safeParseJson(text);
        if (parsed === null) {
            return text;
        }
        return JSON.stringify(normalizeJsonValue(parsed), null, 2);
    };

    const { currentX12Text } = useMemo(() => {
        const normalizedCurrent = toComparableText(currentX12 || '');
        const textArray = normalizedCurrent.split('~');
        return {
            currentX12Text: textArray.join('~\n'),
        };
    }, [currentX12, stripNullFields]);

    const previousX12Text = useMemo(() => {
        const normalizedPrevious = toComparableText(previousX12 || '');
        const previousTextArray = normalizedPrevious.split('~');
        return previousTextArray.join('~\n');
    }, [previousX12, stripNullFields]);

    const diffText = useMemo(() => {
        if (!showDiff || !previousX12) return null;

        const changes = diffLines(previousX12Text, currentX12Text);

        const hunks: string[] = [];
        let oldLineCount = 0;
        let newLineCount = 0;

        changes.forEach(change => {
            if (change.removed) {
                const lines = change.value.split('\n').filter(l => l);
                lines.forEach(line => {
                    hunks.push(`-${line}`);
                    oldLineCount++;
                });
            } else if (change.added) {
                const lines = change.value.split('\n').filter(l => l);
                lines.forEach(line => {
                    hunks.push(`+${line}`);
                    newLineCount++;
                });
            } else {
                const lines = change.value.split('\n').filter(l => l);
                lines.forEach(line => {
                    hunks.push(` ${line}`);
                    oldLineCount++;
                    newLineCount++;
                });
            }
        });

        const diffString = [
            '--- Previous',
            '+++ Current',
            `@@ -1,${oldLineCount} +1,${newLineCount} @@`,
            ...hunks,
        ].join('\n');

        return diffString;
    }, [showDiff, previousX12, previousX12Text, currentX12Text]);

    const { parsedDiff, parseError } = useMemo(() => {
        if (!diffText) return { parsedDiff: null, parseError: null };
        try {
            const files = parseDiff(diffText);
            return { parsedDiff: files[0], parseError: null };
        } catch (e) {
            console.error('Failed to parse payload diff:', e);
            return {
                parsedDiff: null,
                parseError: `Failed to parse transmission comparison: ${e instanceof Error ? e.message : String(e)}`,
            };
        }
    }, [diffText]);

    if (showDiff && parseError) {
        return (
            <S.Container>
                <S.ErrorMessage>{parseError}</S.ErrorMessage>
            </S.Container>
        );
    }

    if (showDiff && parsedDiff) {
        return (
            <S.Container>
                {previousTransmissionId && (
                    <S.TransmissionInfo>Original transmission ID: {previousTransmissionId}</S.TransmissionInfo>
                )}
                <S.LabelsContainer>
                    <S.DiffLabel>{previousLabel}</S.DiffLabel>
                    <S.DiffLabel>{currentLabel}</S.DiffLabel>
                </S.LabelsContainer>
                <S.DiffContainer>
                    <ReactDiffViewer viewType="split" diffType={parsedDiff.type} hunks={parsedDiff.hunks || []}>
                        {hunks => hunks.map((hunk, index) => <Hunk key={`hunk-${index}`} hunk={hunk} />)}
                    </ReactDiffViewer>
                </S.DiffContainer>
            </S.Container>
        );
    }

    if (currentX12) {
        const parsedCurrent = safeParseJson(currentX12Text);
        const canRenderJsonView = parsedCurrent !== null && typeof parsedCurrent === 'object';
        return (
            <S.Container>
                <ScrollableCodeContainer>
                    {canRenderJsonView ? (
                        <JsonView
                            value={parsedCurrent as object}
                            displayDataTypes={false}
                            displayObjectSize={false}
                            enableClipboard={true}
                            objectSortKeys={true}
                            shouldExpandNodeInitially={(_isExpanded, { level }) => level < 3}
                        />
                    ) : (
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '12px' }}>
                            {currentX12Text}
                        </pre>
                    )}
                </ScrollableCodeContainer>
            </S.Container>
        );
    }

    return null;
};
